import React from "react";
import { View } from "react-native";
import { AppIcon, AppText } from "@toothfairy/shared-ui";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import useModelsPricing from "../../Hooks/useModelsPricing";
import { useTranslation } from "react-i18next";

/**
 * ModelPricingInfo - Displays pricing info for LLM, image, video, TTS, or STT models
 * @param {string} modelId - The model identifier
 * @param {string} type - The model type: "llm" (default), "image", "video", "tts", or "stt"
 * @param {object} style - Additional styles
 * @param {boolean} showDynamicRouting - Whether to show dynamic routing badge
 * @param {boolean} showReasonerBadge - Whether to show reasoner badge (only for base model)
 * @param {boolean} showHealthBadge - Whether to show health status badge
 */
const ModelPricingInfo = ({
  modelId,
  type = "llm",
  style,
  showDynamicRouting = true,
  showReasonerBadge = false,
  showHealthBadge = true,
  // Bottom separator line — shown only for fine-tuned models (their pricing
  // block ends the base-model selection area); off by default everywhere else.
  showSeparator = false,
  // Exact pricing for models absent from the templates registry (e.g. a
  // fine-tuned model's flat tier rate). Shape mirrors the hook's return:
  // { modelType, inputUoiPer1mTokens, outputUoiPer1mTokens, maxTokens? }
  pricingOverride,
}) => {
  const { currentTheme } = AppTheme.useTheme();
  const { t } = useTranslation();
  const {
    getModelPricing,
    getImageModelPricing,
    getVideoModelPricing,
    getAudioTtsPricing,
    getAudioTranscriptionPricing,
    formatUoi,
    isLoading,
  } = useModelsPricing();

  // Get pricing based on model type (an override always wins)
  const pricing = pricingOverride
    ? pricingOverride
    : type === "image"
    ? getImageModelPricing(modelId)
    : type === "video"
    ? getVideoModelPricing(modelId)
    : type === "tts"
    ? getAudioTtsPricing(modelId)
    : type === "stt"
    ? getAudioTranscriptionPricing(modelId)
    : getModelPricing(modelId);

  const getModelTypeBadgeColor = (modelType) => {
    switch (modelType) {
      case "extraSmall":
        return currentTheme?.theme?.teal;
      case "small":
        return currentTheme?.theme?.green;
      case "medium":
        return currentTheme?.theme?.blue;
      case "large":
        return currentTheme?.theme?.purple;
      case "extraLarge":
        return currentTheme?.theme?.orange;
      case "ultra":
        return currentTheme?.theme?.primary;
      default:
        return currentTheme?.theme?.grey;
    }
  };

  const getModelTypeLabel = (modelType) => {
    switch (modelType) {
      case "extraSmall":
        return "XS";
      case "small":
        return "S";
      case "medium":
        return "M";
      case "large":
        return "L";
      case "extraLarge":
        return "XL";
      case "ultra":
        return "UL";
      default:
        return modelType || "N/A";
    }
  };

  const getPricingTierBadgeColor = (pricingTier) => {
    switch (pricingTier) {
      case "priority":
        return currentTheme?.theme?.red;
      case "ultrafast":
        return currentTheme?.theme?.pink;
      default:
        return currentTheme?.theme?.yellow;
    }
  };

  const getPricingTierLabel = (pricingTier) => {
    switch (pricingTier) {
      case "priority":
        return "Priority";
      case "fast":
        return "Fast";
      case "ultrafast":
        return "Ultrafast";
      default:
        return pricingTier;
    }
  };

  const getOverallHealth = (health) => {
    if (!health) return null;

    const regions = Object.values(health);
    if (regions.length === 0) return null;

    const hasUnhealthy = regions.some((h) => h && h.status === "unhealthy");
    const hasDegraded = regions.some((h) => h && h.status === "degraded");

    if (hasUnhealthy) return "unhealthy";
    if (hasDegraded) return "degraded";
    return "healthy";
  };

  const getHealthDisplay = (status) => {
    switch (status) {
      case "healthy":
        return {
          icon: AppAssets.icons.check,
          color: currentTheme?.theme?.green,
          labelKey: "common.healthy",
        };
      case "degraded":
        return {
          icon: AppAssets.icons.info,
          color: currentTheme?.theme?.orange,
          labelKey: "common.degraded",
        };
      case "unhealthy":
        return {
          icon: AppAssets.icons.error,
          color: currentTheme?.theme?.white,
          labelKey: "common.unhealthy",
        };
      default:
        return {
          icon: AppAssets.icons.info,
          color: currentTheme?.theme?.grey,
          labelKey: "common.unknown",
        };
    }
  };

  const overallHealth = getOverallHealth(pricing?.health);

  if (!pricingOverride && (isLoading || !pricing)) {
    console.log("[ModelPricingInfo] No pricing data:", { isLoading, pricing });
    return null;
  }

  return (
    <View
      style={[
        {
          paddingVertical: 10,
          paddingHorizontal: 10,
          marginHorizontal: 5,
          marginTop: -4,
          ...(showSeparator && {
            borderBottomWidth: 1,
            borderBottomColor: currentTheme?.theme?.border_color,
          }),
        },
        style,
      ]}
    >
      {/* Single row with size badge and pricing info */}
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        {/* Size Badge */}
        {pricing?.modelType && (
          <View
            style={{
              backgroundColor: getModelTypeBadgeColor(pricing.modelType),
              paddingHorizontal: 10,
              paddingVertical: 4,
              borderRadius: 6,
            }}
          >
            <AppText
              style={{ fontSize: 11, fontWeight: "bold" }}
              color={currentTheme?.theme?.white}
              preventTranslation
            >
              {getModelTypeLabel(pricing.modelType)}
            </AppText>
          </View>
        )}

        {/* Pricing Tier Badge (Priority/Fast/Ultrafast) */}
        {pricing?.pricingTier && (
          <View
            style={{
              backgroundColor: getPricingTierBadgeColor(pricing.pricingTier),
              paddingHorizontal: 10,
              paddingVertical: 4,
              borderRadius: 6,
            }}
          >
            <AppText
              style={{ fontSize: 11, fontWeight: "bold" }}
              color={currentTheme?.theme?.white}
              preventTranslation
            >
              {getPricingTierLabel(pricing.pricingTier)}
            </AppText>
          </View>
        )}

        {/* LLM Token Pricing */}
        {type === "llm" && (
          <>
            {/* Input Pricing */}
            <View style={{ flexDirection: "row", alignItems: "center" }}>
              <AppText
                style={{ fontSize: 12 }}
                color={currentTheme?.theme?.darkGrey}
              >
                Input:
              </AppText>
              <AppText
                style={{ fontSize: 12, fontWeight: "600", marginLeft: 4 }}
                color={currentTheme?.theme?.font_color}
                preventTranslation
              >
                {formatUoi(pricing.inputUoiPer1mTokens)} UoI/1M
              </AppText>
            </View>

            {/* Output Pricing */}
            <View style={{ flexDirection: "row", alignItems: "center" }}>
              <AppText
                style={{ fontSize: 12 }}
                color={currentTheme?.theme?.darkGrey}
              >
                Output:
              </AppText>
              <AppText
                style={{ fontSize: 12, fontWeight: "600", marginLeft: 4 }}
                color={currentTheme?.theme?.font_color}
                preventTranslation
              >
                {formatUoi(pricing.outputUoiPer1mTokens)} UoI/1M
              </AppText>
            </View>

            {/* Cached Input Pricing — shown only when the backend exposes a
                verified model-level cache ratio (cheaper than full input) */}
            {pricing.cachedInputUoiPer1mTokens != null &&
              pricing.cachedInputUoiPer1mTokens <
                pricing.inputUoiPer1mTokens && (
                <View style={{ flexDirection: "row", alignItems: "center" }}>
                  <AppText
                    style={{ fontSize: 12 }}
                    color={currentTheme?.theme?.darkGrey}
                  >
                    {t("models.pricing.cachedInput")}:
                  </AppText>
                  <AppText
                    style={{ fontSize: 12, fontWeight: "600", marginLeft: 4 }}
                    color={currentTheme?.theme?.green}
                    preventTranslation
                  >
                    {formatUoi(pricing.cachedInputUoiPer1mTokens)} UoI/1M
                  </AppText>
                </View>
              )}

            {/* Max Tokens */}
            {pricing.maxTokens && (
              <View style={{ flexDirection: "row", alignItems: "center" }}>
                <AppText
                  style={{ fontSize: 12 }}
                  color={currentTheme?.theme?.darkGrey}
                >
                  Max:
                </AppText>
                <AppText
                  style={{ fontSize: 12, fontWeight: "600", marginLeft: 4 }}
                  color={currentTheme?.theme?.font_color}
                  preventTranslation
                >
                  {pricing.maxTokens?.toLocaleString()}
                </AppText>
              </View>
            )}
          </>
        )}

        {/* Image Pricing */}
        {type === "image" && pricing.perImageUoi && (
          <View style={{ flexDirection: "row", alignItems: "center" }}>
            <AppText
              style={{ fontSize: 12 }}
              color={currentTheme?.theme?.darkGrey}
            >
              Cost:
            </AppText>
            <AppText
              style={{ fontSize: 12, fontWeight: "600", marginLeft: 4 }}
              color={currentTheme?.theme?.font_color}
              preventTranslation
            >
              {formatUoi(pricing.perImageUoi)} UoI/image
            </AppText>
          </View>
        )}

        {/* Video Pricing */}
        {type === "video" && pricing.perVideoUoi && (
          <View style={{ flexDirection: "row", alignItems: "center" }}>
            <AppText
              style={{ fontSize: 12 }}
              color={currentTheme?.theme?.darkGrey}
            >
              Cost:
            </AppText>
            <AppText
              style={{ fontSize: 12, fontWeight: "600", marginLeft: 4 }}
              color={currentTheme?.theme?.font_color}
              preventTranslation
            >
              {formatUoi(pricing.perVideoUoi)} UoI/video
            </AppText>
          </View>
        )}

        {/* TTS Pricing */}
        {type === "tts" && pricing.per1mCharactersUoi && (
          <View style={{ flexDirection: "row", alignItems: "center" }}>
            <AppText
              style={{ fontSize: 12 }}
              color={currentTheme?.theme?.darkGrey}
            >
              Cost:
            </AppText>
            <AppText
              style={{ fontSize: 12, fontWeight: "600", marginLeft: 4 }}
              color={currentTheme?.theme?.font_color}
              preventTranslation
            >
              {formatUoi(pricing.per1mCharactersUoi)} UoI/1M chars
            </AppText>
          </View>
        )}

        {/* STT Pricing */}
        {type === "stt" && pricing.perMinuteUoi && (
          <View style={{ flexDirection: "row", alignItems: "center" }}>
            <AppText
              style={{ fontSize: 12 }}
              color={currentTheme?.theme?.darkGrey}
            >
              Cost:
            </AppText>
            <AppText
              style={{ fontSize: 12, fontWeight: "600", marginLeft: 4 }}
              color={currentTheme?.theme?.font_color}
              preventTranslation
            >
              {formatUoi(pricing.perMinuteUoi)} UoI/min
            </AppText>
          </View>
        )}

        {/* Dynamic Routing Badge */}
        {showDynamicRouting && pricing.dynamicRouting && (
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              backgroundColor: currentTheme?.theme?.green + "20",
              paddingHorizontal: 8,
              paddingVertical: 2,
              borderRadius: 4,
            }}
          >
            <AppIcon
              icon={AppAssets.icons.route}
              size={12}
              color={currentTheme?.theme?.green}
            />
            <AppText
              style={{ fontSize: 11, marginLeft: 4 }}
              color={currentTheme?.theme?.green}
            >
              Dynamic routing
            </AppText>
          </View>
        )}

        {/* Warm Lane Badge (fine-tuned models: flat hourly rate for booked
            lanes — present only when a pricingOverride carries it) */}
        {pricing.warmHourlyUoi != null && (
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              backgroundColor: currentTheme?.theme?.orange + "20",
              paddingHorizontal: 8,
              paddingVertical: 2,
              borderRadius: 4,
            }}
          >
            <AppIcon
              icon={AppAssets.icons.bolt}
              size={12}
              color={currentTheme?.theme?.orange}
            />
            <AppText
              style={{ fontSize: 11, marginLeft: 4 }}
              color={currentTheme?.theme?.orange}
              preventTranslation
            >
              {t("training.serving.warmHourlyRate", {
                rate: formatUoi(pricing.warmHourlyUoi),
              })}
            </AppText>
          </View>
        )}

        {/* Reasoner Badge */}
        {showReasonerBadge && pricing.reasoner && (
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              backgroundColor: currentTheme?.theme?.purple + "20",
              paddingHorizontal: 8,
              paddingVertical: 2,
              borderRadius: 4,
            }}
          >
            <AppIcon
              icon={AppAssets.icons.ai}
              size={12}
              color={currentTheme?.theme?.purple}
            />
            <AppText
              style={{ fontSize: 11, marginLeft: 4 }}
              color={currentTheme?.theme?.purple}
            >
              Reasoner
            </AppText>
          </View>
        )}

        {/* Health Status Badge */}
        {showHealthBadge && overallHealth && (
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              backgroundColor: getHealthDisplay(overallHealth).color + "20",
              paddingHorizontal: 8,
              paddingVertical: 2,
              borderRadius: 4,
            }}
          >
            <AppIcon
              icon={getHealthDisplay(overallHealth).icon}
              size={12}
              color={getHealthDisplay(overallHealth).color}
            />
            <AppText
              style={{ fontSize: 11, marginLeft: 4 }}
              color={getHealthDisplay(overallHealth).color}
            >
              {getHealthDisplay(overallHealth).labelKey}
            </AppText>
          </View>
        )}
      </View>
    </View>
  );
};

export default ModelPricingInfo;
